/**
 * AuthContext - 인증 상태 관리
 *
 * 로그인 상태, 이메일/생일 인증 정보 등 인증 관련 상태를 제공합니다.
 * 로그인, 프로필, 인증 페이지에서 사용됩니다.
 *
 * @CODE:API-201 | 실시간 알림을 위한 customerId, authToken 추가
 * @CODE:API-129 | Firebase 인증 API 연동
 * @CODE:ISSUE-142 | 비회원 주문 병합 기능
 */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { waitForFirebaseUser, waitForFirebaseToken } from '@/lib/auth/waitForFirebaseUser';
import { setUser as setSentryUser } from '@/lib/sentry';
import { apiClient, ApiClientError } from '@/lib/api/client';
import { authApi } from '@/lib/api/auth';
import { userApi } from '@/lib/api/users';
import { pointsApi } from '@/lib/api/points';
import { getFirebaseAuth } from '@/lib/firebase';
import { unregisterPushToken } from '@/lib/push';
import type { AuthUser } from '@/lib/api/types';
import { getSavedGuestCredentials, clearAllGuestCredentials } from '@/utils/password';
import { GuestOrderMergeModal } from '@/components/GuestOrderMergeModal';
import { logger } from '@/utils/logger';
import { safeErrorMetadata } from '@/utils/safeErrorMetadata';

interface AuthContextValue {
  // 상태
  isLoggedIn: boolean;
  /** 로그인 진행 중 여부 */
  isLoggingIn: boolean;
  /** 세션 확인 중 여부 (앱 초기 로드 시) @CODE:ISSUE-150 */
  isCheckingSession: boolean;
  /** 현재 로그인한 사용자 정보 */
  user: AuthUser | null;
  verifiedEmail: string;
  verifiedCompany: string;
  verifiedBirthday: string;
  /** 현재 로그인한 Customer ID (실시간 알림 채널 구독용) */
  customerId: number | null;
  /** 인증 토큰 존재 여부 (보안상 토큰 값은 노출하지 않음) */
  hasAuthToken: boolean;
  /**
   * 포인트 이력 보유 여부 (#285 G1 하단 네비 게이트용).
   * null = 미조회 (비로그인 또는 prefetch 전), true/false = 조회 완료.
   * 로그인 직후 pointsApi.getBalance() 1회 prefetch 로 설정됨.
   */
  hasPointsHistory: boolean | null;
  // 액션
  /** @deprecated firebaseLogin 사용 */
  login: (email: string, password: string) => void;
  /** Firebase ID Token으로 백엔드 로그인 (@CODE:API-129) */
  firebaseLogin: (idToken: string) => Promise<{ success: boolean; error?: string }>;
  /** 서버 세션이 이미 확립된 고객 로그인 결과를 앱 상태에 반영 */
  completeCustomerLogin: (userData: AuthUser) => void;
  logout: () => Promise<void>;
  verifyEmail: (email: string, company: string) => void;
  verifyBirthday: (birthday: string) => void;
  setIsLoggedIn: (value: boolean) => void;
  /** Customer ID 설정 (Firebase 로그인 후 백엔드 응답에서 설정) */
  setCustomerId: (id: number | null) => void;
  /** 인증 토큰 설정 (Firebase ID Token 등) */
  setAuthToken: (token: string | null) => void;
  /** 사용자 프로필 부분 업데이트 (updateProfile 응답 반영용) */
  updateUser: (patch: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  onLoginSuccess?: () => void;
  onLogoutSuccess?: () => void;
}

export function AuthProvider({ children, onLoginSuccess, onLogoutSuccess }: AuthProviderProps) {
  const [isLoggedIn, setIsLoggedInState] = useState<boolean>(false);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [isCheckingSession, setIsCheckingSession] = useState<boolean>(true); // 초기값 true - 세션 확인 중
  const [user, setUser] = useState<AuthUser | null>(null);

  // 인증 데이터 (실제 인증 시 설정됨)
  const [verifiedEmail, setVerifiedEmail] = useState<string>('');
  const [verifiedCompany, setVerifiedCompany] = useState<string>('');
  const [verifiedBirthday, setVerifiedBirthday] = useState<string>('');

  // 실시간 알림을 위한 Customer 정보 (@CODE:API-201)
  const [customerId, setCustomerIdState] = useState<number | null>(null);
  const [hasAuthToken, setHasAuthToken] = useState<boolean>(false);

  // #285: 포인트 이력 보유 여부 (하단 네비 게이트용, 로그인 직후 prefetch)
  const [hasPointsHistory, setHasPointsHistory] = useState<boolean | null>(null);

  // 비회원 주문 병합 모달 상태 (@CODE:ISSUE-142)
  const [showMergeModal, setShowMergeModal] = useState<boolean>(false);
  const [guestCredentials, setGuestCredentials] = useState<{
    phone: string;
    password: string;
  } | null>(null);

  const startupRef = useRef<AbortController | null>(null);
  const authIntentRef = useRef(0);
  const loginSuccessRef = useRef(onLoginSuccess);
  useLayoutEffect(() => { loginSuccessRef.current = onLoginSuccess; }, [onLoginSuccess]);
  const cancelStartup = useCallback((preserveIdentity = false) => {
    authIntentRef.current += 1;
    setIsLoggingIn(false);
    startupRef.current?.abort();
    startupRef.current = null;
    setIsCheckingSession(false);
    apiClient.invalidateAuthScope({ preserveIdentity });
  }, []);

  /**
   * 로컬 세션 정리 (API 호출 없이 상태만 초기화)
   * 401 에러 발생 시 자동 로그아웃에 사용
   */
  const clearSession = useCallback(() => {
    logger.info('[Auth] Session cleared (401 or manual)');
    setIsLoggedInState(false);
    setUser(null);
    setCustomerIdState(null);
    setHasAuthToken(false);
    setHasPointsHistory(null);
    apiClient.setAuthToken(null);
    setSentryUser(null);
    onLogoutSuccess?.();
  }, [onLogoutSuccess]);

  /**
   * 401 에러 핸들러 등록
   * API 요청 중 401 응답 시 자동으로 세션 정리
   */
  useEffect(() => {
    apiClient.setUnauthorizedHandler(clearSession);

    return () => {
      apiClient.setUnauthorizedHandler(null);
    };
  }, [clearSession]);

  /**
   * #285: 로그인 직후 포인트 잔액 prefetch → hasPointsHistory 설정
   * 하단 네비 "포인트" 탭 노출 여부 판단에 사용.
   *
   * isLoggedIn false → true 전이 또는 계정 전환(user.id 변경) 시 호출.
   *
   * #285 P1: 일시 장애 대응으로 실패 시 10 초 후 1회 재시도.
   * 재시도도 실패하면 null 유지 — TabNav 가 fail-open 으로 탭 노출하므로
   * 사용자는 /points 접근 가능, 빈 상태 UI 가 2차 방어.
   */
  useEffect(() => {
    if (!isLoggedIn) {
      setHasPointsHistory(null);
      return;
    }
    // 계정 전환 시 이전 값 즉시 무효화 (새 prefetch 완료 전까지 null)
    setHasPointsHistory(null);

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const attempt = async (isRetry: boolean): Promise<void> => {
      try {
        const response = await pointsApi.getBalance();
        if (cancelled) return;
        if (response.success && response.data) {
          const total = response.data.total_balance ?? 0;
          const hasAccounts = (response.data.accounts?.length ?? 0) > 0;
          setHasPointsHistory(total > 0 || hasAccounts);
        } else {
          setHasPointsHistory(false);
        }
      } catch (err) {
        if (cancelled) return;
        logger.warn('[Auth] hasPointsHistory prefetch 실패:', err, { isRetry });
        if (!isRetry) {
          retryTimer = setTimeout(() => {
            if (!cancelled) void attempt(true);
          }, 10_000);
        } else {
          // 재시도도 실패 → null 유지 (TabNav 가 fail-open 으로 탭 유지)
          setHasPointsHistory(null);
        }
      }
    };

    void attempt(false);

    return () => {
      cancelled = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  }, [isLoggedIn, user?.id]);

  /**
   * 로그인 상태 설정 헬퍼
   * Sanctum 세션 복원 또는 Firebase 재인증 성공 시 호출
   */
  const applyLoginState = useCallback((userData: AuthUser) => {
    apiClient.invalidateAuthScope();
    setUser(userData);
    setCustomerIdState(userData.id);
    setIsLoggedInState(true);
    setHasAuthToken(true);
    setSentryUser({
      id: String(userData.id),
      email: userData.email ?? undefined,
      name: userData.name,
    });
  }, []);

  const showGuestMergePromptIfNeeded = useCallback(() => {
    const savedCredentials = getSavedGuestCredentials();
    if (savedCredentials) {
      setGuestCredentials(savedCredentials);
      setShowMergeModal(true);
    }
  }, []);

  const completeCustomerLogin = useCallback(
    (userData: AuthUser) => {
      cancelStartup();
      applyLoginState(userData);
      showGuestMergePromptIfNeeded();
      onLoginSuccess?.();
    },
    [applyLoginState, onLoginSuccess, showGuestMergePromptIfNeeded, cancelStartup]
  );

  /**
   * 앱 초기 로드 시 기존 세션 복원
   *
   * 1차: Sanctum 세션 쿠키로 복원 시도
   * 2차: 실패 시 Firebase 세션이 살아있으면 idToken 재발급 → firebaseLogin 재시도
   *
   * @CODE:ISSUE-150
   * @see #253 - 브라우저 종료 후 로그인 상태 유지
   */
  useEffect(() => {
    const controller = new AbortController();
    startupRef.current = controller;
    const active = () => startupRef.current === controller && !controller.signal.aborted;
    const checkSession = async () => {
      try {
        // 1차: Sanctum 세션 쿠키로 프로필 복원
        let sanctumExpired = false;

        try {
          const response = await userApi.getProfile(controller.signal);
          if (!active()) return;
          if (response.data) {
            applyLoginState(response.data);
            return;
          }
        } catch (error) {
          if (!active()) return;
          if (error instanceof ApiClientError && (error.status === 401 || error.status === 403)) {
            sanctumExpired = true;
          } else {
            // 네트워크/서버 오류 → 기존 상태 유지, Firebase 재인증도 시도하지 않음
            logger.warn('[Auth] 세션 복원 중 서버/네트워크 오류:', {
              diagnostic_marker: 'menu.auth.profile-restore.v1',
              operation: 'profile-read',
              error: safeErrorMetadata(error) ?? { type: 'Unknown' },
            });
            return;
          }
        }

        // 2차: Sanctum 세션 만료 시 Firebase 세션으로 재인증
        // Firebase SDK는 IndexedDB에서 세션을 비동기로 복원하므로
        // onAuthStateChanged로 복원 완료를 기다려야 함
        if (sanctumExpired) {
          try {
            const auth = getFirebaseAuth();
            if (!auth) {
              logger.info('[Auth] Firebase 미설정, 재인증 건너뜀');
            } else {
              const firebaseUser = await waitForFirebaseUser(auth, controller.signal);
              if (!active()) return;

              if (firebaseUser) {
                logger.info('[Auth] Sanctum 세션 만료, Firebase 세션으로 재인증 시도');
                const idToken = await waitForFirebaseToken(firebaseUser, controller.signal);
                if (!active()) return;
                const response = await authApi.firebaseLogin(idToken, controller.signal);
                if (!active()) return;
                if (response.success && response.user) {
                  applyLoginState(response.user);
                  loginSuccessRef.current?.();
                }
              }
            }
          } catch (firebaseError) {
            if (!active()) return;
            logger.warn('[Auth] Firebase 재인증 실패:', {
              diagnostic_marker: 'menu.auth.profile-restore.v1',
              operation: 'firebase-restore',
              error: safeErrorMetadata(firebaseError) ?? { type: 'Unknown' },
            });
          }
        }
      } finally {
        if (active()) setIsCheckingSession(false);
      }
    };

    void checkSession();
    return () => {
      authIntentRef.current += 1;
      controller.abort();
      if (startupRef.current === controller) startupRef.current = null;
    };
  }, [applyLoginState]);

  // 로그인 (deprecated - firebaseLogin 사용)
  const login = useCallback(
    (email: string, _password: string) => {
      cancelStartup();
      // 보안: 비밀번호는 로깅하지 않음
      logger.info('[Auth] Login attempt for:', email);
      setIsLoggedInState(true);
      // Sentry 사용자 추적 설정
      setSentryUser({ id: email, email });
      onLoginSuccess?.();
    },
    [onLoginSuccess, cancelStartup]
  );

  /**
   * Firebase ID Token으로 백엔드 로그인
   * @CODE:API-129
   */
  const firebaseLogin = useCallback(
    async (idToken: string): Promise<{ success: boolean; error?: string }> => {
      cancelStartup();
      const intent = authIntentRef.current;
      setIsLoggingIn(true);
      try {
        const response = await authApi.firebaseLogin(idToken);
        if (authIntentRef.current !== intent) return { success: false };

        if (response.success && response.user) {
          applyLoginState(response.user);
          showGuestMergePromptIfNeeded();
          onLoginSuccess?.();
          return { success: true };
        }

        return { success: false, error: response.error || response.message };
      } catch (error) {
        if (authIntentRef.current !== intent) return { success: false };
        logger.error('[Auth] Firebase login failed:', error);
        const errorMessage = error instanceof Error ? error.message : '로그인에 실패했습니다';
        return { success: false, error: errorMessage };
      } finally {
        if (authIntentRef.current === intent) setIsLoggingIn(false);
      }
    },
    [applyLoginState, onLoginSuccess, showGuestMergePromptIfNeeded, cancelStartup]
  );

  // 로그아웃
  const logout = useCallback(async () => {
    // Cancel stale reads now; retain the same account ownership for push cleanup.
    cancelStartup(true);
    const intent = authIntentRef.current;
    apiClient.resetCsrf();
    // #680 리뷰: 공유 기기 프라이버시 — 이전 고객의 주문 푸시가 로그아웃 후에도 도착하지 않도록
    // 인증이 살아있을 때(세션 종료 전) 현재 기기 토큰을 해지한다.
    // 실패는 unregisterPushToken 내부에서 삼키므로 로그아웃은 항상 진행된다.
    await unregisterPushToken(() => authIntentRef.current === intent);
    if (authIntentRef.current !== intent) return;

    try {
      // 백엔드 세션 종료
      await authApi.logout();
    } catch (error) {
      if (authIntentRef.current !== intent) return;
      // 로그아웃 API 실패해도 로컬 상태는 초기화
      logger.error('[Auth] Logout API failed:', error);
    }

    // 로컬 상태 초기화
    if (authIntentRef.current === intent) clearSession();
  }, [clearSession, cancelStartup]);

  // 이메일 인증 완료
  const verifyEmail = useCallback((email: string, company: string) => {
    setVerifiedEmail(email);
    setVerifiedCompany(company);
  }, []);

  // 생일 인증 완료
  const verifyBirthday = useCallback((birthday: string) => {
    setVerifiedBirthday(birthday);
  }, []);

  // 로그인 상태 직접 설정 (외부 제어용)
  const setIsLoggedIn = useCallback((value: boolean) => {
    cancelStartup();
    setIsLoggedInState(value);
  }, [cancelStartup]);

  // Customer ID 설정 (@CODE:API-201)
  const setCustomerId = useCallback((id: number | null) => {
    cancelStartup();
    setCustomerIdState(id);
  }, [cancelStartup]);

  // 인증 토큰 설정 (@CODE:API-201)
  const setAuthToken = useCallback((token: string | null) => {
    cancelStartup();
    apiClient.setAuthToken(token);
    setHasAuthToken(token !== null);
  }, [cancelStartup]);

  // 사용자 프로필 부분 업데이트 (프로필 저장 후 AuthContext 동기화용)
  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) {
        // 비로그인 상태에서는 patch를 적용할 대상이 없어 조용히 무시되는데,
        // 호출자는 보통 로그인 사용자를 전제하므로 호출 오류를 관측할 수 있도록 경고.
        logger.warn('[Auth] updateUser called while unauthenticated; patch dropped');
        return prev;
      }
      return { ...prev, ...patch };
    });
  }, []);

  // 비회원 주문 병합 모달 닫기 핸들러 (@CODE:ISSUE-142)
  const handleMergeModalClose = useCallback(() => {
    setShowMergeModal(false);
    setGuestCredentials(null);
    // 병합 여부와 관계없이 credentials 삭제 (다시 묻지 않음)
    clearAllGuestCredentials();
  }, []);

  // 비회원 주문 병합 완료 핸들러 (@CODE:ISSUE-142)
  const handleMergeComplete = useCallback((mergedCount: number) => {
    logger.info(`[Auth] Guest orders merged: ${mergedCount}`);
    // credentials 삭제는 모달 닫을 때 처리됨
  }, []);

  // Context 값 메모이제이션
  const value = useMemo<AuthContextValue>(
    () => ({
      isLoggedIn,
      isLoggingIn,
      isCheckingSession,
      user,
      verifiedEmail,
      verifiedCompany,
      verifiedBirthday,
      customerId,
      hasAuthToken,
      hasPointsHistory,
      login,
      firebaseLogin,
      completeCustomerLogin,
      logout,
      verifyEmail,
      verifyBirthday,
      setIsLoggedIn,
      setCustomerId,
      setAuthToken,
      updateUser,
    }),
    [
      isLoggedIn,
      isLoggingIn,
      isCheckingSession,
      user,
      verifiedEmail,
      verifiedCompany,
      verifiedBirthday,
      customerId,
      hasAuthToken,
      hasPointsHistory,
      login,
      firebaseLogin,
      completeCustomerLogin,
      logout,
      verifyEmail,
      verifyBirthday,
      setIsLoggedIn,
      setCustomerId,
      setAuthToken,
      updateUser,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* 비회원 주문 병합 모달 (@CODE:ISSUE-142) */}
      {guestCredentials && (
        <GuestOrderMergeModal
          open={showMergeModal}
          onClose={handleMergeModalClose}
          phone={guestCredentials.phone}
          password={guestCredentials.password}
          onMergeComplete={handleMergeComplete}
        />
      )}
    </AuthContext.Provider>
  );
}

/**
 * useAuth - AuthContext 사용 훅
 *
 * @throws AuthProvider 외부에서 사용 시 에러
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * useAuthStatus - 인증 상태만 사용
 */
export function useAuthStatus() {
  const { isLoggedIn, isLoggingIn, isCheckingSession, user, verifiedEmail, verifiedCompany, verifiedBirthday } =
    useAuth();
  return { isLoggedIn, isLoggingIn, isCheckingSession, user, verifiedEmail, verifiedCompany, verifiedBirthday };
}

/**
 * useAuthActions - 인증 액션만 사용
 */
export function useAuthActions() {
  const { login, firebaseLogin, logout, verifyEmail, verifyBirthday, setIsLoggedIn } = useAuth();
  return { login, firebaseLogin, logout, verifyEmail, verifyBirthday, setIsLoggedIn };
}
